import { useParams } from "react-router-dom";
import { Alert, Badge, Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table,  } from "reactstrap";
import { useEffect, useRef, useState } from "react";
import { Field, Form, Formik, FormikProps } from "formik";

import { useGetPolicy } from "../../hooks/company-policy/useCompanyPolicy";
import { useGetPolicyNudges } from "../../hooks/company-policy/useCompanyPolicyNudges";
import { addCompanyPolicyNudge } from "../../graphql/company-policy/addCompanyPolicyNudge";
import { updateCompanyPolicyNudge } from "../../graphql/company-policy/updateCompayPolicyNudge";
import { removeCompanyPolicyNudge } from "../../graphql/company-policy/removeCompayPolicyNudge";
import { generateCompanyPolicyNudges } from "../../graphql/company-policy/generateCompanyPolicyNudges";
import { generateOneCompanyPolicyNudge } from "../../graphql/company-policy/generateOneCompanyPolicyNudge";
import { approvalCompanyPolicyNudges } from "../../graphql/company-policy/approvalCompanyPolicyNudges";
import { CompanyPolicy } from "../../interfaces/models/CompanyPolicy";
import { CompanyPolicyNudge } from "../../interfaces/models/CompanyPolicyNudge";

import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import { FileToText } from "../../helper/Utilities";
import FormError from "../../components/Shared/Forms/FormError";
import TextFormInput from "../../components/Shared/Forms/TextFormInput";
import { MAX_COMPANY_POLICY_NUDGES, ValidFileFormatForPolicy } from "../../helper/constants";
import toast from "react-hot-toast";
import { useMutation } from "@apollo/client";
import PromptDialog from "../../components/PromptDialog";
import * as AddCompayPolicyNudgeValidator  from "../../validators/AddCompanyPolicyNudgeValidator";
import TextCounter from "../../components/Shared/Forms/TextCounter";
import CompanyPolicyAndNudgeHistoryModal from "../../components/Compliance/CompanyPolicyAndNudgeHistoryModal";
import CompanyPolicyNudgeTable from "../../components/Compliance/CompanyPolicyNudgeTable";

interface RouteParams {
  id: string;
}

interface FormData {
  title: string;
  description: string;
}

interface Breadcrumb {
  name: string;
  path: string;
}

interface ModalParams {
  name: string;
  action: string;
}

const CompanyPolicyDetailsPage = () => {
  const { id } = useParams<RouteParams>();

  const [breadcrumbsData, setBreadcrumbsData] = useState<Breadcrumb[]>([
    { name: "Complaince", path: `/compliance` },
    { name: "Policy Hub", path: `/compliance/policy-hub` },
  ]);

  const [policy, setPolicy] = useState<CompanyPolicy>(null);
  const [nudges, setNudges] = useState<CompanyPolicyNudge[]>([]);
  const [isDataReady, setIsDataReady] = useState<boolean>(false);

  const [isReloading, setIsReloading] = useState(false);

  const [modal, setModal] = useState<ModalParams>({
    name: "Add",
    action: "add",
  });
  const [isAddNudgeOpen, setIsAddNudgeOpen] = useState<boolean>(false);
  const [selectedNudge, setSelectedNudge] = useState<CompanyPolicyNudge>(null);
  const [isOnProcess, setIsOnProcess] = useState<boolean>(false);
  const formikRef = useRef<FormikProps<FormData>>(null);
  const [initialValuePolicy, setInitialValuePolicy] = useState<FormData>({
    title: "",
    description: "",
  });

  const [isDeleteNudgeOpenModal, setIsDeleteNudgeOpenModal] = useState<boolean>(false);

  const [isApproveNudgeOpenModal, setIsApproveNudgeOpenModal] = useState<boolean>(false);

  const [step, setStep] = useState(0);
  const [policyMetaData, setPolicyMetaData] = useState("");
  const [nudgeCount, setNudgeCount] = useState(0);
  const [generatedNudges, setGeneratedNudges] = useState<CompanyPolicyNudge[]>([]);
  const [approvedNudges, setApprovedNudges] = useState<string[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<CompanyPolicy>(null);
  
  const [selectedNudgeHistory, setSelectedNudgeHistory] = useState<CompanyPolicyNudge>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState<boolean>(false);

  const [isGenerateNudgeOpen, setIsGenerateNudgeOpen] = useState<boolean>(false);

  const { data, loading, error, refetch } = useGetPolicy(id);
  const fetchPolicyNudges = useGetPolicyNudges(id);

  const [addCompanyPolicyNudgeHook] = useMutation(addCompanyPolicyNudge, {
    onCompleted: () => {
      toast.success("Company Policy Nudge was successfully added");
    },
    onError: () => {
      toast.error("Something went wrong whilst Adding the Company Policy Nudge. Please try again or if issue persists, please contact us");
    },
  });
  const [updateCompanyPolicyNudgeHook] = useMutation(updateCompanyPolicyNudge, {
    onCompleted: () => {
      toast.success("Company Policy Nudge was successfully updated");
    },
    onError: () => {
      toast.error("Something went wrong whilst updating the Company Policy Nudge. Please try again or if issue persists, please contact us");
    },
  });
  const [removeCompanyPolicyNudgeHook] = useMutation(removeCompanyPolicyNudge, {
    onCompleted: () => {
      toast.success(`Company Policy Nudge was successfully removed`);
    },
    onError: () => {
      toast.error("Something went wrong whilst removing the Company Policy Nudge. Please try again or if issue persists, please contact us");
    },
  });
  
  const [generateCompanyPolicyNudgesHook] = useMutation(generateCompanyPolicyNudges, {
    onCompleted: () => {
      toast.success("Company Policy Nudges was successfully Generated");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const [generateOneCompanyPolicyNudgeHook] = useMutation(generateOneCompanyPolicyNudge, {
    onCompleted: () => {
      toast.success("Company Policy Nudge was successfully Regenerated");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const [approvalCompanyPolicyNudgesHook] = useMutation(approvalCompanyPolicyNudges, {
    onCompleted: () => {
      toast.success("Company Policy Nudge Approvel was successfully finished");
    },
    onError: () => {
      toast.error("Something went wrong whilst finising the approval of Company Policy Nudge. Please try again or if issue persists, please contact us");
    },
  });

  const reloadData = async () => {
    setIsReloading(true);
    await refetch();
    setIsReloading(false);
  };
  const reloadNudges = async () => {
    setIsReloading(true);
    await fetchPolicyNudges.refetch();
    setIsReloading(false);
  };

  useEffect(() => {
    if (data) {
      const getPolicyRaw = data.company_policy;
      setPolicy(getPolicyRaw);

      setBreadcrumbsData((prev) => [...prev, { name: getPolicyRaw.name, path: `/company-policies/${id}` }]);
    }
  }, [data, id]);

  useEffect(() => {
    if (fetchPolicyNudges.data) {
      const nudgesRaw = fetchPolicyNudges.data.company_policy_nudges;
      setNudges(nudgesRaw);
    }
  }, [fetchPolicyNudges.data]);

  useEffect(() => { 
    if(fetchPolicyNudges.loading === false && loading === false) {
      setIsDataReady(true);
    }
  },[fetchPolicyNudges.loading, loading])

  //#region Add/Update Nudge Manually
  const handleOpenAddNudge = () => {
    setInitialValuePolicy({
      title: "",
      description: "",
    });
    setModal({
      name: "Add",
      action: "add",
    });
    setIsAddNudgeOpen(true);
  };
  const handleOpenEditNudge = (item: CompanyPolicyNudge) => {
    setInitialValuePolicy({
      title: item.title || "",
      description: item.description || "",
    });
    setModal({
      name: "Update",
      action: "update",
    });
    setSelectedNudge(item);
    setIsAddNudgeOpen(true);
  };

  const submitAddNudge = async (value: FormData) => {
    if (isOnProcess) return;

    setIsOnProcess(true);

    let result = null;

    if (modal.action === "add") {
      result = await addCompanyPolicyNudgeHook({
        variables: {
          companyPolicyId: policy._id,
          title: value.title,
          description: value.description,
        },
      })
        .then(res => (res.errors ? false : true))
        .catch(() => false);
    } else if (modal.action === "update") {
      result = await updateCompanyPolicyNudgeHook({
        variables: {
          nudgeId: selectedNudge._id,
          title: value.title,
          description: value.description,
        },
      })
        .then(res => (res.errors ? false : true))
        .catch(() => false);
    }

    setIsOnProcess(false);
    if (!result) return;
    setIsAddNudgeOpen(false);
    await reloadNudges();
  };
  //#endregion

  //#region Delete Nudge
  const handleOpenDeleteNudge = (item: CompanyPolicyNudge) => {
    setSelectedNudge(item);
    setIsDeleteNudgeOpenModal(true);
  };
  const handleDeleteNudge = async () => {
    if (isOnProcess) return;
    setIsOnProcess(true);
    const result = await removeCompanyPolicyNudgeHook({
      variables: {
        nudgeId: selectedNudge._id,
      },
    })
      .then(res => (res.errors ? false : true))
      .catch(() => false);
    setIsOnProcess(false);
    if (!result) return;
    setIsDeleteNudgeOpenModal(false);
    await reloadNudges();
  };
  //#endregion

  //#region Individual Approval 
  const handleOpenSingleApprove = (item: CompanyPolicyNudge) => {
    setSelectedNudge(item);
    setIsApproveNudgeOpenModal(true);
  }

  const handleSingleApprove = async () => {
    if (isOnProcess) return;

    setIsOnProcess(true);
    
    const result = await approvalCompanyPolicyNudgesHook({
      variables: {
        companyPolicyId: policy._id,
        rejectedIds: [],
        approvedIds: [selectedNudge._id]
      }
    });
    setIsOnProcess(false);

    if(!result.data) return;
    setNudges(result.data.CompanyPolicyNudge_Approval);
    setIsApproveNudgeOpenModal(false);
  }
  //#endregion

  //#region Generate Nudge
  const handleOpenGenerateNudge = () => {
    setIsGenerateNudgeOpen(true);
    setNudgeCount(MAX_COMPANY_POLICY_NUDGES - nudges.length)
    setSelectedPolicy(policy);
  };

  const handleStepOneGenerate = async () => {
    if(isOnProcess) return;

    setIsOnProcess(true);

    const result = await generateCompanyPolicyNudgesHook({
      variables: {
        companyPolicyId: policy._id,
        nudgeCount: nudgeCount,
        metaData: policyMetaData,
        policyName: selectedPolicy.policy_file_name
      }
    });
    setIsOnProcess(false);
    if(!result.data) { 
      return;
    }
    setGeneratedNudges(result.data.CompanyPolicyNudge_Generate as CompanyPolicyNudge[]);
    setStep(1);
    setIsOnProcess(false); 
    if(policyMetaData) {
      await reloadData();
    }


  }

  const handleRegenerateNudge = async (id: string, onGenerated = true) => {
    if(isOnProcess) return;

    setIsOnProcess(true);

    const result = await generateOneCompanyPolicyNudgeHook({
      variables: {
        nudgeId: id,
      }
    });
    setIsOnProcess(false);
    if(!result.data) return;
    
    if(onGenerated) {
      setGeneratedNudges(prev =>
        prev.map(u =>
          u._id === id
            ? (result.data.CompanyPolicyNudge_GenerateOne as CompanyPolicyNudge)
            : u
        )
      );
    }
    else {
      setNudges(prev =>
        prev.map(u =>
          u._id === id
            ? (result.data.CompanyPolicyNudge_GenerateOne as CompanyPolicyNudge)
            : u
        )
      );
    }
    removeFromArray(id, approvedNudges, setApprovedNudges);
  }

  const removeFromArray = (id: string, array: string[], set: (value:string[]) => void) => {
    const shallowCopy = [...array];
    const getIndexRejected = shallowCopy.indexOf(id);
    if(getIndexRejected !== -1) {
      shallowCopy.splice(getIndexRejected, 1);
      set(shallowCopy);
    }
  }

  const handleApproveNudge = (id: string, approve: boolean = true) => {
    const shallowCopy = [...approvedNudges];
    const getIndex = shallowCopy.indexOf(id);
    if(approve) {
      shallowCopy.push(id)
    }
    else {
      shallowCopy.splice(getIndex, 1);
    }
    setApprovedNudges(shallowCopy);
    
  }

  const submitGenerateNudge = async () => {
    if (isOnProcess) return;
    if(approvedNudges.length === 0) {
      toast.error("Please Approve/Reject all the nudges to proceed");
    }
    setIsOnProcess(true);
    
    const result = await approvalCompanyPolicyNudgesHook({
      variables: {
        companyPolicyId: policy._id,
        approvedIds: approvedNudges
      }
    });
    

    if(!result.data) {
      setIsOnProcess(false);
      return;
    }

    setNudges(result.data.CompanyPolicyNudge_Approval);
    setStep(0);
    setApprovedNudges([]);
    setIsGenerateNudgeOpen(false);
    setIsOnProcess(false);
  };
  //#endregion

  //#region Nudge History 
  const handleOpenNudgeHistory = (item: CompanyPolicyNudge) => {
    setIsHistoryModalOpen(true); 
    setSelectedNudgeHistory(item);
  }
  //#endregion

  return (
    <>
      <Breadcrumbs crumbs={breadcrumbsData} />
      {error && <Alert color="danger">Oops, we’re having trouble getting the Policy. Please give it a minute, and if it doesn’t resolve, let us know.</Alert>}
      <div className="d-flex justify-content-between mb-3">
        <h3>Nudges <Badge color="info">{nudges.length}/5</Badge></h3>
        {
          nudges.length !== MAX_COMPANY_POLICY_NUDGES ?
          <div>
            <Button color="primary" onClick={handleOpenGenerateNudge} disabled={isOnProcess || !isDataReady}>
              Generate
            </Button>
            <Button color="primary" onClick={handleOpenAddNudge} disabled={isOnProcess || !isDataReady}>
              Add Nudge
            </Button>
          </div>
          :
          <></>
        }
      </div>
      <CompanyPolicyNudgeTable 
        loading={loading || isReloading}
        isOnProcess={isOnProcess}
        nudges={nudges}
        hasCRUD={true}
        policy={policy}
        handleOpenNudgeHistory={handleOpenNudgeHistory}
        handleOpenDeleteNudge={handleOpenDeleteNudge}
        handleOpenEditNudge={handleOpenEditNudge}
        handleOpenSingleApprove={handleOpenSingleApprove}
        handleRegenerateNudge={handleRegenerateNudge}
      />

      <Modal id="policy-generate-nudge-modal" backdrop={true} isOpen={isGenerateNudgeOpen} size={ step === 0 ? "" :"xl"}>
        <ModalHeader toggle={() => setIsGenerateNudgeOpen(prev => !prev)} className="pb-0">
          <p className="text-lg">Generate Nudges</p>
        </ModalHeader>
        <ModalBody className="pt-0 mb-0">
          {step === 0 && (
            <div>
              <h4>Configurations</h4>
              <div className="form">
                <FormGroup>
                  <Label>No. of Nudges to Generate *</Label>
                  <Input id="exampleSelect" type="select" 
                    value={nudgeCount}
                    onChange={(e) => {
                      setNudgeCount(Number(e.target.value))
                    }}
                  >
                    {
                      Array.from({ length: MAX_COMPANY_POLICY_NUDGES - nudges.length }).reverse().map((u, index)  => {
                        const max = MAX_COMPANY_POLICY_NUDGES - nudges.length;
                        const value = max - (index );
                        return <option key={index} value={value}>{value}</option>
                      })
                    }
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label>Reference File * </Label>
                  {
                    selectedPolicy?.policy_file_name ?
                    
                    <Row>
                      <Col md={8} >
                        <small className="description-details">{selectedPolicy.policy_file_name}</small>
                      </Col>
                      <Col md={4} className="text-right">
                        <small className="text-danger" onClick={() => { setSelectedPolicy((prev) => ({...prev, policy_file_name: ""})) }} role="button"> <i className="fas fa-times"></i> Remove</small>
                      </Col>
                    </Row>
                    :
                    <>
                      <Input
                        name="file"
                        type="file"
                        accept={ValidFileFormatForPolicy}
                        className="form-control"
                        onChange={async e => {
                          const file = e.target.files?.[0];
                          if (!file) {
                            setSelectedPolicy((prev) => ({...prev, policy_file_name: null}));
                            setPolicyMetaData("");
                            return;
                          }
                          setSelectedPolicy((prev) => ({...prev, policy_file_name: file.name}))
                          const text = await FileToText(file);
                          setPolicyMetaData(text);
                        }}
                      />
                      <small className="text-muted">Please upload a policy file to Proceed</small>
                    </>
                  }
                  
                </FormGroup>
              </div>
            </div>
          )}
          {step === 1 && (
            <>
              <span>Notes:</span>
              <ul className="small">
                <li>If the nudge contents does not meet your standards, click “Regenerate” to get a new one.</li>
                <li>We’ll apply approved nudges to your scheduled compliance nudges and unapproved ones will stay for futher review</li>
                <li>You may make changes to the content after it has been approved.</li>
              </ul>
              <Table striped responsive bordered style={{tableLayout: "fixed"}}>
                <colgroup>
                  <col width="20%"></col>
                  <col width="30%"></col>
                  <col width="20%"></col>
                </colgroup>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Description</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  { 
                    generatedNudges.map((u) => (
                      <tr key={u._id}>
                        <td>
                          <span className="description-details">
                            {u.title}
                          </span>
                        </td>
                        <td >
                          <span className="description-details">
                            {u.description}
                          </span>
                        </td>
                        <td>
                          {
                            !approvedNudges.includes(u._id) ?
                              <Button onClick={() => handleApproveNudge(u._id, true)} size="sm">
                                Approve
                              </Button>
                            :
                              <Button onClick={() => handleApproveNudge(u._id, false)} size="sm" color="success">
                                Approved
                              </Button>
                          }
                          <Button size="sm" color="primary" disabled={isOnProcess} onClick={() =>handleRegenerateNudge(u._id)}>
                            Regenerate
                          </Button>
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </Table>

            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsGenerateNudgeOpen(false)} disabled={isOnProcess}>
            Close
          </Button>
          {step === 0 && (
            <Button
              color="primary"
              onClick={handleStepOneGenerate}
              disabled={isOnProcess || !selectedPolicy?.policy_file_name}
            >
              Generate
            </Button>
          )}
          {step === 1 && (
            <Button color="primary" onClick={submitGenerateNudge} disabled={isOnProcess || approvedNudges.length === 0 }>
              Finalise
            </Button>
          )}
        </ModalFooter>
      </Modal>

      <Modal id="policy-add-nudge-modal" backdrop={true} isOpen={isAddNudgeOpen}>
        <ModalHeader toggle={() => setIsAddNudgeOpen(prev => !prev)} className="pb-0">
          <p className="text-lg">{modal.name} Nudge</p>
        </ModalHeader>
        <ModalBody>
          <Formik
            innerRef={formikRef}
            initialValues={initialValuePolicy}
            validationSchema={AddCompayPolicyNudgeValidator.AddCompayPolicyNudgeValidator}
            onSubmit={async (value: FormData) => {
              await submitAddNudge(value);
            }}
          >
            {({ errors, touched, values }) => (
              <Form>
                <TextFormInput name="title" placeholder="Enter title" label="Title" required={true} />
                <TextCounter maxLength={AddCompayPolicyNudgeValidator.MAX_TITLE_LENGTH} text={values.title}  />
                <div className="form-group">
                  <label>Description *</label>
                  <Field 
                    className="form-control" name="description" placeholder="Enter description" as="textarea"
                    rows={2}
                    onKeyDown={(e) => {
                      const value = e.target.value;
                      const newLines = (value.match(/\n/g) || []).length;
                      if (e.key === "Enter" && newLines >= 1) {
                        e.preventDefault();
                      }
                    }}
                    style={{resize:"none"}}
                  />
                  <TextCounter maxLength={AddCompayPolicyNudgeValidator.MAX_DESCRIPTION_LENGTH} text={values.description}  />
                  <FormError>{touched.description && errors.description}</FormError>
                </div>
              </Form>
            )}
          </Formik>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsAddNudgeOpen(false)} disabled={isOnProcess}>
            Close
          </Button>
          <Button color="primary" onClick={() => formikRef.current.submitForm()} disabled={isOnProcess}>
            {modal.name}
          </Button>
        </ModalFooter>
      </Modal>
      
      <PromptDialog
        isOpen={isApproveNudgeOpenModal}
        onCancel={() => setIsApproveNudgeOpenModal(false)}
        onConfirm={handleSingleApprove}
        title="Approve Nudge"
        promptMessage="Once approved, this nudge will be available to use on your scheduled nudges."
        confirmBtnTxt="Approve"
        isOnProcess={isOnProcess}
        onConfirmButtonColor="bg-success"
      />

      <PromptDialog
        isOpen={isDeleteNudgeOpenModal}
        onCancel={() => setIsDeleteNudgeOpenModal(false)}
        onConfirm={handleDeleteNudge}
        title={`Delete Policy Nudge`}
        promptMessage="Action is Irreversible, nudge will be removed permanently"
        confirmBtnTxt={"Delete"}
        isOnProcess={isOnProcess}
      />

      {selectedNudgeHistory?._id && 
      <CompanyPolicyAndNudgeHistoryModal
        isOpen={isHistoryModalOpen}
        toggle={() =>{ setIsHistoryModalOpen((prev) => !prev); setSelectedNudgeHistory(null)}}
        nudgeId={selectedNudgeHistory?._id}
      />}
    </>
  );
};

export { CompanyPolicyDetailsPage as CompanyPolicyDetails };
